import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import useActivationPaymentSettings, {
    ActivationPaymentSettings,
    PaymentRecord,
    PaymentReportData,
} from '@/src/Activations/components/hooks/useActivationPaymentSettings';
import { useEventContext } from '@/src/v2/components/EventContext';
import EventPage from '../chrome/EventPage';
import { Crumb, Field, SaveBar, SectionCard, TextInput } from '../components/forms';
import { PageHeadActions } from '../components/nav';
import { useToast } from '../components/roster';
import { Segmented, TCard, TEmpty, TFoot, TSkeleton } from '../components/table';
import '../pages-event.css';

/*
 * Payments — paid photo sessions for one event. Stripe keys are
 * write-only: the secret key is never shown, and an empty field keeps
 * whatever is stored. The server gates settings and reports to admins.
 */

type Range = '7' | '30' | '90' | 'custom';
type Env = 'test' | 'live';

const RANGES: Array<{ value: Range; label: string }> = [
    { value: '7', label: '7 days' },
    { value: '30', label: '30 days' },
    { value: '90', label: '90 days' },
    { value: 'custom', label: 'Custom' },
];

const PAGE = 50;

interface Draft {
    cost: string;
    environment: Env;
    publishableKey: string;
    secretKey: string;
}

const dollars = (cents: number) =>
    (cents / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' });

const plural = (n: number, word: string) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

function draftFrom(data: ActivationPaymentSettings | null): Draft {
    return {
        cost: data ? (data.costInCents / 100).toFixed(2) : '',
        environment: data?.stripeEnvironment === 'live' ? 'live' : 'test',
        publishableKey: data?.stripePublishableKey ?? '',
        secretKey: '',
    };
}

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'ok' | 'bad' }) {
    return (
        <div className="tile">
            <p className="tl">{label}</p>
            <div className="tn">{value}</div>
            {sub && <div className={tone ? `ts ${tone}` : 'ts'}>{sub}</div>}
        </div>
    );
}

/** Field at full card width (keys are long). */
function WideField({ label, help, error, children }: { label: string; help?: string; error?: string; children: React.ReactNode }) {
    return (
        <label className="field wide ev-gap">
            <span className="field-l">{label}</span>
            {children}
            {error ? <span className="field-e">{error}</span> : help && <span className="field-h">{help}</span>}
        </label>
    );
}

function RevenueChart({ days }: { days: PaymentReportData['dailyChartData'] }) {
    const W = 600;
    const H = 96;
    const max = Math.max(1, ...days.map((d) => d.revenue));
    const slot = W / days.length;
    const bar = Math.max(1, slot * 0.72);
    return (
        <>
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="Revenue by day">
                <line x1="0" y1={H - 0.5} x2={W} y2={H - 0.5} stroke="color-mix(in oklab, var(--base) 8%, transparent)" strokeWidth="1" />
                {days.map((d, i) => {
                    const h = Math.max(2, (d.revenue / max) * (H - 6));
                    return (
                        <rect
                            key={d.date}
                            x={i * slot + (slot - bar) / 2}
                            y={H - h}
                            width={bar}
                            height={h}
                            rx="1.5"
                            fill="color-mix(in oklab, var(--base) 74%, transparent)"
                            opacity={d.revenue > 0 ? 1 : 0.25}
                        >
                            <title>{`${moment(d.date).format('MMM D')} · ${dollars(d.revenue)} · ${plural(d.count, 'payment')}`}</title>
                        </rect>
                    );
                })}
            </svg>
            <div className="g-x">
                <span>{moment(days[0].date).format('MMM D')}</span>
                <span>{moment(days[days.length - 1].date).format('MMM D')}</span>
            </div>
        </>
    );
}

function PaymentsBody() {
    const navigate = useNavigate();
    const { activationId: id } = useEventContext();
    const {
        getActivationPaymentSettings,
        createActivationPaymentSettings,
        updateActivationPaymentSettings,
        getPaymentReportData,
        getPaymentRecords,
    } = useActivationPaymentSettings();
    const toast = useToast();

    const [settings, setSettings] = useState<ActivationPaymentSettings | null>(null);
    const [denied, setDenied] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [baseline, setBaseline] = useState<Draft>(() => draftFrom(null));
    const [draft, setDraft] = useState<Draft>(() => draftFrom(null));
    const [errors, setErrors] = useState<Partial<Record<'cost' | 'publishableKey' | 'secretKey', string>>>({});
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const [range, setRange] = useState<Range>('30');
    const [custom, setCustom] = useState({ start: '', end: '' });
    const [report, setReport] = useState<PaymentReportData | null>(null);
    const [records, setRecords] = useState<PaymentRecord[]>([]);
    const [reportLoading, setReportLoading] = useState(false);
    const [reportAttempt, setReportAttempt] = useState(0);
    const [limit, setLimit] = useState(PAGE);

    useEffect(() => {
        if (!id) return;
        let mounted = true;
        getActivationPaymentSettings(id)
            .then((data) => {
                if (!mounted) return;
                setSettings(data);
                setBaseline(draftFrom(data));
                setDraft(draftFrom(data));
                setIsLoading(false);
            })
            .catch(() => {
                if (!mounted) return;
                setDenied(true);
                setIsLoading(false);
            });
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!settings) return;
        if (range === 'custom' && (!custom.start || !custom.end)) return;
        let mounted = true;
        const start = range === 'custom' ? custom.start : moment().subtract(+range, 'days').format('YYYY-MM-DD');
        const end = range === 'custom' ? custom.end : moment().format('YYYY-MM-DD');
        setReportLoading(true);
        Promise.all([
            getPaymentReportData(id, start, end).catch(() => null),
            getPaymentRecords(id, start, end).catch(() => []),
        ]).then(([reportData, recordRows]) => {
            if (!mounted) return;
            setReport(reportData);
            setRecords(recordRows ?? []);
            setLimit(PAGE);
            setReportLoading(false);
        });
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings?.id, range, custom.start, custom.end, reportAttempt]);

    const dirty = useMemo(() => {
        const sections: string[] = [];
        if (draft.cost !== baseline.cost) sections.push('Pricing');
        if (
            draft.environment !== baseline.environment ||
            draft.publishableKey !== baseline.publishableKey ||
            draft.secretKey !== ''
        )
            sections.push('Stripe');
        return sections;
    }, [draft, baseline]);

    const edit = <K extends keyof Draft>(key: K, value: Draft[K]) => {
        setDraft((d) => ({ ...d, [key]: value }));
        setErrors((e) => ({ ...e, [key]: undefined }));
        setSaveError(null);
    };

    const save = async () => {
        if (saving) return;
        const next: typeof errors = {};
        const cents = Math.round(parseFloat(draft.cost) * 100);
        if (!Number.isFinite(cents) || cents <= 0) next.cost = 'Cost per photo must be a positive amount.';
        if (draft.publishableKey.trim() && !/^pk_(test|live)_/.test(draft.publishableKey.trim()))
            next.publishableKey = 'Publishable key should start with pk_test_ or pk_live_.';
        if (draft.secretKey.trim() && !/^sk_/.test(draft.secretKey.trim()))
            next.secretKey = 'Secret key should start with sk_.';
        setErrors(next);
        if (Object.keys(next).length > 0) return;

        setSaving(true);
        setSaveError(null);
        const body: any = {
            activationId: id,
            costInCents: cents,
            stripeEnvironment: draft.environment,
        };
        if (draft.publishableKey.trim()) body.stripePublishableKey = draft.publishableKey.trim();
        if (draft.secretKey.trim()) body.stripeSecretKey = draft.secretKey.trim();
        const result = settings
            ? await updateActivationPaymentSettings(body).catch(() => null)
            : await createActivationPaymentSettings(body).catch(() => null);
        setSaving(false);
        if (result) {
            const kept: Draft = { ...draft, secretKey: '' };
            setSettings(result);
            setBaseline(kept);
            setDraft(kept);
            toast.show(settings ? 'Payment settings saved.' : 'Payments enabled.');
        } else {
            setSaveError('Couldn’t save — try again.');
        }
    };

    const discard = () => {
        setDraft(baseline);
        setErrors({});
        setSaveError(null);
    };

    const weekDelta =
        report && report.paymentsPreviousWeek > 0
            ? Math.round(((report.paymentsCurrentWeek - report.paymentsPreviousWeek) / report.paymentsPreviousWeek) * 100)
            : null;

    const sub = isLoading
        ? 'Loading…'
        : denied
          ? 'Admin access required'
          : settings
            ? `${dollars(settings.costInCents)} per photo · Stripe ${settings.stripeEnvironment === 'live' ? 'live' : 'test'}`
            : 'Paid photo sessions are off — set a price to turn them on';

    return (
        <div className="ev-page">
            <Crumb label="Overview" onClick={() => navigate(`/activations/${id}`)} />
            <PageHeadActions title="Payments" sub={sub} />

            {isLoading ? (
                <TCard>
                    <TSkeleton rows={3} />
                </TCard>
            ) : denied ? (
                <TCard>
                    <TEmpty message="Payments needs an admin account — ask an org admin for access." />
                </TCard>
            ) : (
                <>
                    <div className="stack">
                        <SectionCard title="Pricing">
                            <Field label="Cost per photo (USD)" error={errors.cost}>
                                <TextInput
                                    inputMode="decimal"
                                    placeholder="5.00"
                                    style={{ maxWidth: 140 }}
                                    value={draft.cost}
                                    disabled={saving}
                                    aria-invalid={errors.cost ? true : undefined}
                                    onChange={(e) => edit('cost', e.target.value)}
                                />
                            </Field>
                        </SectionCard>

                        <SectionCard title="Stripe" note={settings?.hasStripeKeys ? 'custom keys configured' : 'using the system Stripe account'}>
                            <div className="field">
                                <span className="field-l">Environment</span>
                                <Segmented<Env>
                                    label="Stripe environment"
                                    value={draft.environment}
                                    onChange={(v) => edit('environment', v)}
                                    options={[
                                        { value: 'test', label: 'Test' },
                                        { value: 'live', label: 'Live' },
                                    ]}
                                />
                            </div>
                            <WideField label="Publishable key" help="Optional — blank uses the system default." error={errors.publishableKey}>
                                <TextInput
                                    className="input ev-mono"
                                    placeholder="pk_test_…"
                                    value={draft.publishableKey}
                                    disabled={saving}
                                    aria-invalid={errors.publishableKey ? true : undefined}
                                    onChange={(e) => edit('publishableKey', e.target.value)}
                                />
                            </WideField>
                            <WideField
                                label="Secret key"
                                help={
                                    settings?.hasStripeKeys
                                        ? 'A secret key is stored and never shown. Leave empty to keep it.'
                                        : 'Optional — never shown again after saving.'
                                }
                                error={errors.secretKey}
                            >
                                <TextInput
                                    type="password"
                                    autoComplete="new-password"
                                    className="input ev-mono"
                                    placeholder={settings?.hasStripeKeys ? 'Unchanged unless set' : 'sk_test_…'}
                                    value={draft.secretKey}
                                    disabled={saving}
                                    aria-invalid={errors.secretKey ? true : undefined}
                                    onChange={(e) => edit('secretKey', e.target.value)}
                                />
                            </WideField>
                            {saveError && <p className="field-e">{saveError}</p>}
                        </SectionCard>
                    </div>

                    <SaveBar dirty={dirty} saving={saving} onSave={save} onDiscard={discard} />

                    {settings && (
                        <div className="sec" style={{ marginTop: 16 }}>
                            <div className="sec-h">
                                <h2>Report</h2>
                            </div>
                            <div className="toolbar">
                                <Segmented<Range> label="Report range" value={range} onChange={setRange} options={RANGES} />
                                {range === 'custom' && (
                                    <>
                                        <TextInput
                                            type="date"
                                            aria-label="Start date"
                                            style={{ maxWidth: 160 }}
                                            value={custom.start}
                                            max={custom.end || undefined}
                                            onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))}
                                        />
                                        <TextInput
                                            type="date"
                                            aria-label="End date"
                                            style={{ maxWidth: 160 }}
                                            value={custom.end}
                                            min={custom.start || undefined}
                                            onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))}
                                        />
                                    </>
                                )}
                            </div>

                            {range === 'custom' && (!custom.start || !custom.end) ? (
                                <p className="quiet-empty">Pick a start and end date.</p>
                            ) : reportLoading ? (
                                <TSkeleton rows={3} />
                            ) : (
                                <>
                                    {report ? (
                                        <>
                                            <div className="ev-tiles">
                                                <Tile label="Last 24h" value={dollars(report.revenueLast24h)} sub={plural(report.paymentsLast24h, 'payment')} />
                                                <Tile
                                                    label="This week"
                                                    value={dollars(report.revenueCurrentWeek)}
                                                    sub={weekDelta != null ? `${weekDelta >= 0 ? '+' : ''}${weekDelta}% vs last week` : undefined}
                                                    tone={weekDelta == null ? undefined : weekDelta >= 0 ? 'ok' : 'bad'}
                                                />
                                                <Tile label="This month" value={dollars(report.revenueCurrentMonth)} />
                                                <Tile label="Lifetime" value={dollars(report.revenueLifetime)} sub={plural(report.paymentsLifetime, 'payment')} />
                                                <Tile
                                                    label="Sessions"
                                                    value={report.totalSessions.toLocaleString()}
                                                    sub={`${report.totalClaimed} claimed · ${report.totalInvalidated} invalidated`}
                                                />
                                            </div>
                                            <div className="graph">
                                                <div className="g-h">
                                                    <span className="g-leg">
                                                        <span className="sw" style={{ background: 'var(--text-secondary)' }} />
                                                        Revenue
                                                    </span>
                                                    <span className="g-when">by day</span>
                                                </div>
                                                {report.dailyChartData.length > 1 ? (
                                                    <RevenueChart days={report.dailyChartData} />
                                                ) : (
                                                    <p className="g-empty">Not enough days in this range for a chart.</p>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <TEmpty
                                            message="Couldn’t load the report."
                                            actionLabel="Retry"
                                            onAction={() => setReportAttempt((a) => a + 1)}
                                        />
                                    )}

                                    <div className="sec-h" style={{ marginTop: 8 }}>
                                        <h2>Payments</h2>
                                        <span className="n">{records.length.toLocaleString()}</span>
                                    </div>
                                    <TCard>
                                        {records.length === 0 ? (
                                            <TEmpty message="No payments in this range." />
                                        ) : (
                                            records.slice(0, limit).map((r) => {
                                                const state = r.invalidatedAt ? 'Invalidated' : r.claimedAt ? 'Claimed' : 'Paid';
                                                const tone = r.invalidatedAt ? 'ev-st bad' : r.claimedAt ? 'ev-st ok' : 'ev-st muted';
                                                return (
                                                    <div className="trow ev-row" key={r.id}>
                                                        <span className="main">
                                                            <span className="nm">{dollars(r.costInCents)}</span>
                                                            <span className="sub">
                                                                {[moment(r.paidAt).format('MMM D, h:mm A'), r.redemptionCode, r.photoSessionId]
                                                                    .filter(Boolean)
                                                                    .join(' · ')}
                                                            </span>
                                                        </span>
                                                        <span className={tone}>{state}</span>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </TCard>
                                    {records.length > 0 && (
                                        <TFoot>
                                            <div className="ev-foot">
                                                <span>
                                                    {Math.min(limit, records.length).toLocaleString()} of {records.length.toLocaleString()}
                                                </span>
                                                {records.length > limit && (
                                                    <button type="button" className="tb-btn" onClick={() => setLimit((l) => l + PAGE)}>
                                                        Show more
                                                    </button>
                                                )}
                                            </div>
                                        </TFoot>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </>
            )}
            {toast.node}
        </div>
    );
}

export default function PaymentsV3() {
    return (
        <EventPage section="payments">
            <PaymentsBody />
        </EventPage>
    );
}
